import { useContext } from 'react'
import { createContext, useEffect, useState } from 'react'
import translations from '../public/it.json'

type LocaleKey = keyof typeof translations
export type LocalizedString = typeof translations[LocaleKey]

export enum Language {
  it = 'Italiano',
  de = 'Deutsche',
  fr = 'Français',
  es = 'Español'
}

interface LoadingTranslationsContext {
  isLoading: true
}

interface ReadyTranslationsContext {
  isLoading: false
  translate: (
    key: LocaleKey,
    values?: Record<string, string>
  ) => LocalizedString
  currentLanguage: keyof typeof Language
}

type TranslationsContext = LoadingTranslationsContext | ReadyTranslationsContext

const TranslationsContext = createContext<TranslationsContext>({
  isLoading: true
})

interface Props {
  children: JSX.Element
}

export function useTranslationsContext() {
  return useContext(TranslationsContext)
}

export function useTranslate(): (key: LocaleKey) => string {
  const translationsContext = useTranslationsContext()

  if (translationsContext.isLoading) {
    return () => {
      throw new Error('Translations context is loading. Something is wrong')
    }
  } else {
    return translationsContext.translate
  }
}

export function useLanguage(): keyof typeof Language {
  const translationsContext = useTranslationsContext()

  if (translationsContext.isLoading) {
    return 'it'
  } else {
    return translationsContext.currentLanguage
  }
}

function getTranslation(
  translations: Record<LocaleKey, LocalizedString>,
  key: LocaleKey,
  values?: Record<string, string>
) {
  if (!translations[key]) {
    return key
  }

  return Object.entries(values || {}).reduce(
    (translation, [key, value]) => translation.replaceAll(`{${key}}`, value),
    translations[key]
  )
}

export function TranslationsContextProvider(props: Props) {
  const language = getLanguage()

  const [translations, setTranslations] = useState<Record<
    LocaleKey,
    LocalizedString
  > | null>(null)

  useEffect(() => {
    setTranslations(null)

    window
      .fetch(`${process.env.PUBLIC_URL}/${language}.json`)
      .then(response => response.json())
      .then(response => {
        setTranslations(response)
      })
  }, [language])

  const contextValue: TranslationsContext =
    translations === null
      ? {
          isLoading: true
        }
      : {
          isLoading: false,
          currentLanguage: language,
          translate: (key, values) => getTranslation(translations, key, values)
        }

  return (
    <TranslationsContext.Provider value={contextValue}>
      {props.children}
    </TranslationsContext.Provider>
  )
}

function getLanguage(): keyof typeof Language {
  const languageFromDomain = getLanguageFromDomain()
  const langSearchParam = new URLSearchParams(window.location.search).get(
    'lang'
  )

  if (languageFromDomain) {
    return languageFromDomain
  } else if (
    langSearchParam &&
    Object.keys(Language).includes(langSearchParam)
  ) {
    return langSearchParam as keyof typeof Language
  } else {
    return getBrowserLanguage()
  }
}

function getLanguageFromDomain(): keyof typeof Language | null {
  const domain = window.location.host

  if (domain.includes('mconst')) {
    return null
  }

  const domainCountry = domain.slice(-2)

  if (Object.keys(Language).includes(domainCountry)) {
    return domainCountry as keyof typeof Language
  } else {
    return null
  }
}

function getBrowserLanguage(): keyof typeof Language {
  const browserLanguage = navigator.language.substring(0, 2)

  if (
    Object.keys(Language as Record<string, string>).includes(browserLanguage)
  ) {
    return browserLanguage as keyof typeof Language
  } else {
    return 'it'
  }
}
